'use client';

import { createContext, useContext, useMemo, useState, PropsWithChildren, useEffect, useCallback } from 'react';
import { getDealStatusFilterItems, getFiltersDealStatus } from '../helpers';
import { DealStatus, IDealsPayload } from '@/creditflowresearch/domain/vo/IDeal';
import isEqual from 'lodash/isEqual';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { useOpenStatusGroups } from '@/creditflowresearch/application/Deal/openStatusGroups/useOpenStatusGroups';

const PLAN_GATED_STATUSES: DealStatus[] = [DealStatus.Priced];

const useDealStatuses = ({ filters }: IProps) => {
  const { includedStatusGroups } = filters || {};
  const getInitialStatuses = useCallback(
    () => includedStatusGroups?.length ? includedStatusGroups : Object.values(DealStatus)
  , [includedStatusGroups]);
  const [selectedStatuses, setSelectedStatuses] = useState<DealStatus[]>(getInitialStatuses());
  const { plan } = useUserPlanContext();
  // TODO: remove when the European market opening period ends (US-5562)
  const hasOpenStatusGroups = useOpenStatusGroups();
  const hasAllStatusGroups = hasOpenStatusGroups || UserPlan.isPro(plan);

  const filterStatusSet = useMemo(() => ({
    dealStatus: {
      items: getDealStatusFilterItems(selectedStatuses, plan, hasOpenStatusGroups),
      isVisible: true,
    },
  }), [selectedStatuses, plan, hasOpenStatusGroups]);

  useEffect(() => {
    if (!hasAllStatusGroups) {
      setSelectedStatuses((prevState) => (
        isEqual(prevState, PLAN_GATED_STATUSES) ? prevState : PLAN_GATED_STATUSES
      ));
      return;
    }

    const initialDealStatuses = getInitialStatuses();
    if (isEqual(initialDealStatuses, selectedStatuses)) return;

    setSelectedStatuses(initialDealStatuses);
  }, [
    filters?.includedProductIds,
    filters?.includedRegionIds,
    filters?.includedStatusGroups,
    getInitialStatuses,
    hasAllStatusGroups,
  ]);

  const applyDealStatus = useCallback((_filterName: string, dealStatus: DealStatus): void => {
    if (!hasAllStatusGroups) return;
    setSelectedStatuses((prevState) => getFiltersDealStatus(prevState, dealStatus));
  }, [hasAllStatusGroups]);

  return {
    filterStatusSet,
    selectedStatuses,
    applyDealStatus,
  };
};

type DealStatusContextType = ReturnType<typeof useDealStatuses>;

export const DealStatusContext = createContext<DealStatusContextType>(null);

export const useDealStatusContext = () => {
  const context = useContext(DealStatusContext);
  if (!context) {
    throw new Error('useDealStatusContext must be used within the DealStatusContext');
  }
  return context;
};

interface IProps {
  filters: IDealsPayload['filters'],
}

const DealStatusContextProvider = ({ children, filters }: PropsWithChildren<IProps>) => (
  <DealStatusContext.Provider value={ useDealStatuses({ filters }) }>
    { children }
  </DealStatusContext.Provider>
);

export default DealStatusContextProvider;
